<template>
  <div class="progress">
    <div class="container" v-if="snapshotsMissing">
      <div class="rank-zone__no-data">Looks like you don’t have any ranking data to report.</div>
    </div>
    <div class="container" v-else-if="!isDataFetched">
      <div class="rank-zone__loader">
        <base-loader />
      </div>
    </div>
    <div v-if="isDataUpdating"
         class="page-cover-loader">
      <base-loader />
    </div>

    <div class="container" v-if="isDataFetched">
      <div class="progress__row">
            <AreaChart class="progress__chart"
                       ref="chart"
                       :selectedType="tableType"
                       :cells="filteringCells"
                       :snapshots="filteredSnapshots"
                       @updateData="updateData"
                       @web-property="onWebPropertyGroupChanged">
            </AreaChart>
      </div>

      <base-content-block :title="tableTitle">
        <template #title-additional>
          <div class="progress__table-type-selection">
            <base-radio
              v-model="tableType"
              style="margin-right: 15px;"
              value="msv"
              label="MSV"
              class="progress__table-type-selection__radio-button"
              crossColor="#8F90A6"/>
            <base-radio
              v-model="tableType"
              value="keyword"
              label="# KW"
              class="progress__table-type-selection__radio-button"
              crossColor="#8F90A6"/>
          </div>
        </template>
<!--        <template #title-additional>-->
<!--          <base-date-picker-button :availableDates="availableDates"-->
<!--                                   title="Choose Comparison Date"-->
<!--                                   :removeCaret="true"-->
<!--                                   :bigDatePicker="true"-->
<!--                                   :customPickerState="{placement: 'bottom'}"-->
<!--                                   v-model="comparisonDate"/>-->
<!--        </template>-->
        <div class="progress__table" ref="table">
          <div class="progress__table-header">
            <div class="progress__table-column_top">Rank Zone</div>
            <template v-for="(name, index) in columnLabels">
              <div v-if="index === 0" class="progress__table-column_first progress__table-cell" :key="name">
                <base-check-box :label="firstColumnTitle"
                                v-model="isAllCheckboxesSelected"
                                @change="onAllSnapshotsSelectedToggle"
                >
                  <div style="height: 26px; width: 34px;"></div>
                </base-check-box>
              </div>
              <div v-else class="progress__table-cell"
                   :key="name"
                   :class="[index < numberColumnsAmount ? `progress__table-column_center-${index}` : 'progress__table-column_last']">
                {{name}}
              </div>
            </template>
          </div>
          <template v-if="!isDataAwaiting">
            <template v-for="item in progressSnapshots">
              <div :key="item.label"
                  class="progress__table-row"
                  :class="{'progress__table-row__selected': isDateSelected(item.label)}">
                <template v-for="(cell, index) in tableConf.cells">
                  <div v-if="index === 0"
                       class="progress__table-column_first progress__table-cell progress__table-cell_clickable"
                       :key="cell.propName">
                    <base-check-box
                      :label="item['label']"
                      :withRadioButton="true"
                      :isRadioButtonSelected="isDateSelected(item.label)"
                      @radioButtonChange="updateComparisonDate($event, item['label'])"
                      v-model="selectedSnapshots[item['label']]"/>
                  </div>
                  <div v-else
                       class="progress__table-cell"
                       :key="cell.propName"
                       :class="[index < numberColumnsAmount ? `progress__table-column_center-${index}` : 'progress__table-column_last']">
                    <span
                      v-tooltip.top-middle="{content: getTooltipText(item, cell.propName), ...progressTooltipOptions}"
                      :class="{'progress__table-cell_clickable': cell.propName != 'total'}"
                      @click="openModal(cell, item.label)">
                      {{getValue(item[tableType], cell) | commaDividedNumber}}
                    </span>
                  </div>
                </template>
              </div>
            </template>
          </template>
          <template v-else>
            <div class="rank-zone__no-data progress__no-data">No records found</div>
          </template>
<!--          <div class="progress__update-loader" v-if="isDataUpdating">-->
<!--            <base-loader class="progress__update-loader-ring"/>-->
<!--          </div>-->
        </div>
      </base-content-block>
    </div>
    <modal name="compareMovement"
           v-bind="compareModalProps"
    >
      <CompareRankZoneModal :clientWebPropertyName="clientWebPropertyName"
                            :clientWebPropertyId="clientWebPropertyId"
                            :queryData="clickedItem"
                            @close="closeModal('compareMovement')"/>
    </modal>
    <modal name="movement"
           v-bind="modalProps"
    >
      <KeywordMovementTable :clickedItem="clickedItem"
                            :cellName="cellName"
                            :showSnapshotData="showSnapshotData"
                            :isBaseLine="isBaseLine"
                            :webPropertyGroupId="webPropertyGroupSelected._id"
                            :item="clickedItem"
                            :isReport="true"
                            :advancedFilters="advancedOptionsToFilters"
                            @close="closeModal('movement')"/>
    </modal>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import { baseComponent } from '@/features/reports/common/Mixins'
import AreaChart from '@/features/reports/common/AreaChart'
import csvExport from '@/services/CSVExport'
import moment from 'moment'
import filters from '@/common/Filters'
import { convertAdvancedOptionsToFilters } from '@/features/reports/common/advanced-options/config'
import Helper from '@/services/Helper'
import baseModalProps from '@/common/BaseModalProps'
import CompareRankZoneModal from '@/features/reports/rank-zone/competition/CompareRankZoneModal'

/**
 * Progress page.
 * Used in RankZone
 */
export default {
  name: 'Progress',
  mixins: [baseComponent],
  props: {
    /**
     * Advanced options from parent element
     */
    selectedAdvancedOptions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      firstColumnTitle: 'Rank Zone Snapshot',
      isAllCheckboxesSelected: true,
      /**
       *  web property groups selected from {@link AreaChart}
       */
      webPropertyGroupSelected: {},
      /**
       * snapshots selected from the table
       */
      selectedSnapshots: {},
      tableConf: {
        cells: [
          {
            label: 'Rank Zone Snapshot',
            propName: 'label'
          }, {
            label: '01 - 03',
            propName: '01 - 03'
          }, {
            label: '04 - 07',
            propName: '04 - 07'
          }, {
            label: '08 - 10',
            propName: '08 - 10'
          }, {
            label: '11 - 25',
            propName: '11 - 25'
          }, {
            label: '26 - 50',
            propName: '26 - 50',
            disabledByDefault: true
          }, {
            label: '51 - 100',
            propName: '51 - 100',
            disabledByDefault: true
          }, {
            label: 'NR',
            propName: 'NR',
            disabledByDefault: true
          }, {
            label: 'ND',
            propName: 'ND',
            disabledByDefault: true
          }, {
            label: 'Total',
            propName: 'total'
          }
        ]
      },
      /**
       * Selected comparison date
       */
      comparisonDate: null,
      clickedItem: {},
      cellName: null,
      showSnapshotData: false,
      isBaseLine: false,
      tableType: 'msv'
    }
  },
  components: {
    AreaChart,
    CompareRankZoneModal,
    KeywordMovementTable: () => import('@/features/content-briefs/brief-content/KeywordMovementTable')
  },
  computed: {
    ...mapState('rankZoneProgress', {
      progressSnapshots (state) {
        return _.get(state, 'progress', [])
      }
      // availableDates (state) {
      //   const snashotDates = _.get(state, 'snapshot_dates', [])
      //   let avaliableDates = snashotDates.map(date => moment(date).toDate())
      //   avaliableDates = _.sortBy(avaliableDates).reverse()
      //   return avaliableDates
      // }
    }),
    ...mapState('rankZoneCommon', {
      webPropertyGroups (state) {
        return state.webPropertyGroups
      },
      clientWebProperty (state) {
        const webPropertyGroups = _.cloneDeep(state.webPropertyGroups)
        return (webPropertyGroups.find(i => i.type === 'Clients') || {})
      },
      clientWebPropertyName () {
        return this.clientWebProperty.label
      },
      clientWebPropertyId () {
        return this.clientWebProperty._id
      }
    }),
    compareModalProps () {
      const shareModalProps = {
        width: '1200px'
      }
      return { ...baseModalProps, ...shareModalProps }
    },
    filteredSnapshots () {
      return this.progressSnapshots.filter(item => this.selectedSnapshots[item.label])
    },
    columnLabels () {
      return this.tableConf.cells.map(item => item.label)
    },
    progressTooltipOptions () {
      const options = {
        classes: 'rfx-tooltip-arrow'
      }
      return Object.assign({}, this.tooltipOptions, options)
    },
    columnPropertyNames () {
      return this.tableConf.cells.map(item => item.propName)
    },
    filteringCells () {
      return this.tableConf.cells.slice(1, this.numberColumnsAmount)
    },
    numberColumnsAmount () {
      return this.tableConf.cells.length - 1
    },
    isDataAwaiting () {
      return this.isDataFetched && !this.isDataUpdating && (!this.progressSnapshots || this.progressSnapshots.length === 0)
    },
    advancedOptionsToFilters () {
      return convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
    },
    tableTitle () {
      const competitorSiblingName = this.webPropertyGroupSelected.type.toLowerCase() !== 'clients'
        ? `(${this.webPropertyGroupSelected.label})`
        : null
      return `Temporal Overall Ranking${competitorSiblingName ? ` <span style="font-size: 14px;">${competitorSiblingName}</span>` : ''}`
    },
    modalProps () {
      const shareModalProps = {
        width: '1200px'
      }
      return { ...baseModalProps, ...shareModalProps }
    }
  },
  watch: {
    selectedClient: {
      handler (value) {
        if (value && value.changedFromNavbar) {
          this.resetProgressData()
          this.resetData()
        }
      }
    },
    selectedAdvancedOptions: {
      async handler () {
        this.isDataFetched && await this.updateData()
      },
      deep: true
    },
    comparisonDate: {
      handler (newValue) {
        this.updateData()
        if (newValue && newValue !== 'clear') {
          this.$nextTick(() => {
            const element = this.$refs.table.querySelector('.progress__table-row__selected')
            if (element) {
              const scroll = window.scrollY || window.pageYOffset
              const boundsTop = element.getBoundingClientRect().top + scroll

              const viewport = {
                top: scroll,
                bottom: scroll + window.innerHeight
              }

              const bounds = {
                top: boundsTop,
                bottom: boundsTop + element.clientHeight
              }

              if ((bounds.bottom >= viewport.top && bounds.bottom <= viewport.bottom) ||
                (bounds.top <= viewport.bottom && bounds.top >= viewport.top)) {

              } else {
                element.scrollIntoView({ block: 'end', behavior: 'smooth' })
              }
            }
          })
        }
      }
    },
    progressSnapshots (value = []) {
      if (_.isEmpty(this.selectedSnapshots)) {
        // Vue reactivity stuff
        value.forEach(item => {
          this.$set(this.selectedSnapshots, item.label, true)
        })
      }
    },
    /**
     * change header checkbox depending on table checkboxes state
     * */
    selectedSnapshots: {
      handler () {
        this.isAllCheckboxesSelected = Object.values(this.selectedSnapshots).some(item => item)
      },
      deep: true
    },
    /**
     * trigger for loading page data
     */
    webPropertyGroups: {
      handler (newGroups, oldGroups) {
        if (!newGroups || !newGroups.length) {
          return
        }
        if (Helper.isArraysEquals(newGroups, oldGroups)) {
          return
        }
        this.webPropertyGroupSelected = newGroups[0] || {}
        this.$nextTick(this.loadPageData)
      },
      immediate: true
    },
    clientId: {
      handler () {
        this.comparisonDate = 'clear'
      }
    }
  },
  methods: {
    ...mapActions('rankZoneProgress', [
      'getProgressData',
      'resetProgressData'
    ]),
    async loadPageData () {
      if (!this.snapshotsPresent) {
        return
      }
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      await this.getProgressData({
        clientId: this.clientId,
        filters: { advancedFilters, web_property_group_id: this.webPropertyGroupSelected._id }
      })
      this.isDataFetched = true
    },
    updateComparisonDate (value, date) {
      this.comparisonDate = value ? moment(date).toDate() : 'clear'
    },
    resetData () {
      this.$refs['chart'] && this.$refs['chart'].reset()
      this.isDataFetched = false
      this.isAllCheckboxesSelected = true
      this.webPropertyGroupSelected = {}
      this.selectedSnapshots = {}
      this.screenshotName = ''
    },
    getValue (item, cell) {
      return item[cell.propName] ? item[cell.propName] : 0
    },
    async updateData () {
      if (!this.webPropertyGroupSelected._id) {
        return
      }
      if (this.isDataFetched) {
        this.isDataUpdating = true
      }
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      await this.getProgressData({
        clientId: this.clientId,
        filters: {
          advancedFilters,
          web_property_group_id: this.webPropertyGroupSelected._id,
          snapshot_date: this.comparisonDate && this.comparisonDate !== 'clear'
            ? moment(this.comparisonDate).format('YYYY-MM-DD')
            : '1970-01-01'
        }
      })
      this.isDataUpdating = false
      this.isDataFetched = true
    },
    /**
     * callback for header checkbox that manages all table checkboxes state
     */
    onAllSnapshotsSelectedToggle () {
      Object.keys(this.selectedSnapshots).forEach(key => {
        this.selectedSnapshots[key] = this.isAllCheckboxesSelected
      })
    },
    isDateSelected (date) {
      const rowDate = moment(date).format('YYYY-MM-DD')
      if (rowDate === moment(this.comparisonDate).format('YYYY-MM-DD')) {
        return true
      } else {
        return false
      }
    },
    exportCsv () {
      if (this.isDataUpdating || this.snapshotsMissing) {
        return
      }
      const data = []
      let header = []
      this.columnLabels.forEach(name => {
        header.push(`${name.replace('-', 'to')}`)
      })
      header[0] = `${_.get(this.webPropertyGroupSelected, 'label', '')} ${header[0]}`
      data.push(header.map(item => `"${item}"`))
      this.progressSnapshots.forEach(item => {
        const row = []
        this.columnPropertyNames.forEach(name => {
          const data = name === 'label' ? item[name] : item[this.tableType][name]
          row.push(`"${data}"`)
        })
        data.push(row)
      })
      csvExport(`${this.selectedClient.name.replace(/ /g, '_')}_RankZone_${this.$route.name}_${moment().format('YYYY-MM-DD')}`, data)
    },
    /**
     * callback for {@link AreaChart}
     * Invoked by changing web property group in dropdown
     * @param group+++
     */
    onWebPropertyGroupChanged (group) {
      if (group.value === this.webPropertyGroupSelected._id) {
        return
      }
      this.webPropertyGroupSelected = this.webPropertyGroups.find(item => item._id === group.value) || {}
      this.updateData()
    },
    /**
     * Opens {@link KeywordMovementTable} modal
     * @param cellName
     * @param date
     */
    openModal (cell, date) {
      if (this.comparisonDate && moment(this.comparisonDate).format('YYYY-MM-DD') !== date) {
        this.cellName = cell.label
        this.clickedItem = {}
        this.clickedItem.rank_zone = cell.propName.replace(' - ', '_')
        this.clickedItem.snapshot_date_from = moment(date === 'clear' ? '1970-01-01' : date).format('YYYY-MM-DD')
        this.clickedItem.snapshot_date_to = moment(this.comparisonDate).format('YYYY-MM-DD')
        if (!this.comparisonDate || this.comparisonDate === 'clear') {
          this.showSnapshotData = true
          this.isBaseLine = false
        } else if (this.clickedItem.snapshot_date_from === this.clickedItem.snapshot_date_to) {
          this.showSnapshotData = true
          this.isBaseLine = true
        } else {
          this.showSnapshotData = false
          this.isBaseLine = false
        }
        this.$modal.show('movement')
      } else {
        let advancedFilters = {}
        if (this.selectedAdvancedOptions && this.selectedAdvancedOptions.length) {
          advancedFilters = this.advancedOptionsToFilters
        }
        this.clickedItem = {
          'web_property': this.webPropertyGroupSelected._id,
          'date': date,
          'rank_zone': cell.propName,
          webPropertyName: this.webPropertyGroupSelected.label,
          advancedFilters
        }
        this.$modal.show('compareMovement')
      }
    },
    closeModal (name) {
      this.$modal.hide(name)
    },
    getTooltipText (item, cellName) {
      const tooltipType = this.tableType === 'msv' ? 'keyword' : 'msv'
      const tooltipTypeText = tooltipType === 'msv' ? 'MSV' : 'Keyword Count'
      return `${tooltipTypeText}: ${filters.commaDividedNumber(item[tooltipType][cellName])}`
    }
  },
  filters: { ...filters }
}

</script>

<style scoped lang="scss">
  $columns: 8;

  @for $i from 0 through $columns {
    .progress___table-column_center-#{$i} {
      grid-area: center-#{$i};
    }
  }

  .progress {
    margin-bottom: -20px;

    &__no-data {
      height: 90px;
    }

    &__advanced {
      margin: 14px 0;
    }

    &__table-type-selection {
      display: flex;
      align-items: center;

      &__radio-button {
        cursor: pointer;

        &::v-deep .radio__label {
          font-size: 18px;
          font-weight: 500;
        }
      }
    }

    &__row {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      margin-bottom: 14px;

      &-item {
        flex: 1 1 100%;

        &:first-child {
        }
      }
    }

    &__chart-wrapper {
      width: 100%;
      padding-top: 20px;
      box-sizing: border-box;
    }

    &__chart {
    }

    &__table {
      flex: 1 1 100%;
      flex-direction: column;

      &-cell {
        padding-left: 30px;
      }

      &-header {
        color: $rfx-dark-grey;
        font-weight: 500;
        background: $rfx-pale-grey;
        flex: 1 1 100%;
        display: grid;
        grid-template-columns: 220px repeat(8, 1fr) 180px;
        grid-template-rows: 50px 50px;
        grid-template-areas: "first top top top top top top top top last" "first center0 center1 center2 center3 center4 center5 center6 center7 last";
        align-items: center;
      }

      &-row {
        @extend .progress__table-header;
        grid-template-areas: "first center0 center1 center2 center3 center4 center5 center6 center7 last";
        font-weight: normal;
        grid-template-rows: 50px;
        background-color: #fff;
        color: $rfx-light-march;
        border-bottom: 1px solid $rfx-almost-white;

        .progress__table-cell {
          pointer-events: none;
        }

        .progress__table-cell_clickable {
          pointer-events: all !important;

          &:hover {
            text-decoration: underline;
            cursor: pointer;
            color: $rfx-bright-blue;
          }
        }
      }

      &-row__selected {
        background-color: $rfx-light-grey !important;

        .progress__table-cell {
          background-color: $rfx-light-grey !important;
        }
      }

      &-column {
        &_first {
          grid-area: first;
          font-weight: 500;
          font-size: 14px;

          &:hover {
            text-decoration: none !important;
            cursor: default !important;
          }
        }

        &_last {
          grid-area: last;
          font-weight: 500;
          font-size: 14px;
        }

        &_top {
          grid-area: top;
          border-bottom: 1px solid rgba(0, 0, 0, .05);
          height: 100%;
          width: 100%;
          display: grid;
          place-content: center;
        }
      }
    }

    &__update-loader {
      position: absolute;
      display: flex;
      justify-content: center;
      background-color: white;
      opacity: 0.5;
      z-index: 10000;
      margin: auto;
      top: 0;
      width: 100%;
      height: 100%;
    }

    &__update-loader-ring {
      margin: auto 0;
    }
  }

</style>
